import { createRouteDestinationFeature } from "../utils/createRouteDestinationFeature";
import { useCallback, useContext, useEffect } from "react";
import i18n from "../i18next";
import {
  FeatureQueryResponse,
  RouteQueryResponse,
} from "./../redux/services/types/index";
import { useParams } from "react-router-dom";

import { usePostAnalyticsEvent } from "./usePostAnalyticsEvent";

import { generateFullRouteMilestones } from "./../utils/generateFullRouteMilestones";
import { generateFullRoute } from "../utils/generateFullRoute";
import createLMFeature from "../utils/createLMFeature";
import createLMLocationFeature from "../utils/createLMLocationFeature";
import { AnalyticsEvent } from "../utils/analyticsTypes";
import { buildRouteData } from "../utils/buildRouteData";

import { MapContext } from "../contexts/MapContext";

import { FloorConfig } from "../components/Map/plugins/floor-control";

interface Props {
  segments?: RouteQueryResponse["segments"];
  sequenceOrder?: number[];
  directionsEnabled?: boolean;
  originFeature?: FeatureQueryResponse | string[];
  destinationFeature?: FeatureQueryResponse | string[];
  intercept?: RouteQueryResponse["intercept"];
  activeFloor?: FloorConfig;
  stepFreeRoute: boolean;
}

export const useDisplayRoute = ({
  segments,
  sequenceOrder,
  directionsEnabled,
  originFeature,
  destinationFeature,
  intercept,
  activeFloor,
  stepFreeRoute,
}: Props) => {
  const { fromId, toId } = useParams();
  const { logAnalyticsEvent } = usePostAnalyticsEvent();

  const { routingControl, interactionControl } = useContext(MapContext);

  const routeMilestones =
    segments && sequenceOrder
      ? generateFullRouteMilestones(sequenceOrder, segments, directionsEnabled) // combines all routeMilestones into one array
      : [];

  const zoomToRouteMilestone = (index: number) => {
    if (routeMilestones.length) {
      routingControl?.handleMapRouteMilestoneDisplay(routeMilestones[index]);
    }
  };

  const endpointKey = JSON.stringify([
    originFeature,
    destinationFeature,
    intercept,
    i18n.language,
  ]);
  const renderRoute = useCallback(() => {
    interactionControl?.deselectFeatures();
    interactionControl?.clearSelectedFeatureSource();

    if (!segments) routingControl?.clear();
    if (
      segments &&
      sequenceOrder &&
      originFeature &&
      (destinationFeature || intercept)
    ) {
      const features = generateFullRoute(sequenceOrder, segments);
      const milestones = generateFullRouteMilestones(
        sequenceOrder,
        segments,
        directionsEnabled,
      );
      const totalLength = segments.reduce(
        (acc, segment) => acc + segment.length,
        0,
      );
      routingControl?.renderRoute(features, milestones, totalLength);

      const formatOriginFeature =
        Array.isArray(originFeature) && originFeature?.length === 2
          ? createLMLocationFeature(originFeature)
          : "id" in originFeature
            ? createLMFeature(originFeature, i18n.language)
            : null;

      const formatDestinationFeature = intercept
        ? createRouteDestinationFeature(
            intercept.effectiveDestination,
            i18n.language,
          )
        : Array.isArray(destinationFeature) && destinationFeature?.length === 2
          ? createLMLocationFeature(destinationFeature)
          : destinationFeature && "id" in destinationFeature
            ? createLMFeature(destinationFeature, i18n.language)
            : null;

      formatOriginFeature &&
        formatDestinationFeature &&
        routingControl?.renderRoutePins([
          formatOriginFeature,
          formatDestinationFeature,
        ]);

      if (!fromId || !toId) return;

      const { fromData, toData } = buildRouteData({
        fromId,
        toId,
        floor: activeFloor,
      });

      logAnalyticsEvent({
        event_type: AnalyticsEvent.ROUTE_SHOWN,
        event_data: {
          ...fromData,
          ...toData,
          step_free: stepFreeRoute,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpointKey,
    routingControl,
    segments,
    sequenceOrder,
    directionsEnabled,
    activeFloor,
    interactionControl,
  ]);

  useEffect(renderRoute, [renderRoute]);

  return { zoomToRouteMilestone, routeMilestones, renderRoute };
};
